// Article registry. Each module in this folder is a plain object with
// { slug, title, date, description, keywords, tldr, readingTime, provenance,
//   provenanceUrl, series, artifact, markdown }.
//
// Optional media fields:
//   hero  = { src, alt, caption }         a lead image under the title
//   video = "https://youtu.be/..."        YouTube, Vimeo, or mp4 URL
//   Inline images use standard markdown ![caption](url); the alt becomes the caption.
//   provenanceUrl = the original LinkedIn post URL (renders the provenance line as a link).
//
// Order here controls display order on the /articles page (newest first).
// readingTime is auto-computed from word count when a module leaves it null.

import orangeRibbonHunt from "./orange-ribbon-hunt";
import momentumOneSmallThing from "./momentum-one-small-thing";
import guardrailReviewBathroomLockout from "./guardrail-review-bathroom-lockout";
import nobelExplainerAgentPipeline from "./nobel-explainer-agent-pipeline";
import measuringAiPrinciples from "./measuring-ai-principles";
import theAccountabilitySplit from "./the-accountability-split";
import raciDaciDelay from "./raci-daci-delay";
import softwareSupplyChainRisk from "./software-supply-chain-risk";
import humaneInTheLoop from "./humane-in-the-loop";
import oneMorePrompt from "./one-more-prompt";
import proofOfWorkVsImposter from "./proof-of-work-vs-imposter-syndrome";
import singaporeFrontRowSeat from "./singapore-front-row-seat";
import tokenOptimizingClaudeCode from "./token-optimizing-claude-code";
import owaspAgentSecurityGap from "./owasp-agent-security-gap";
import gcpSuspensionAiSecurity from "./gcp-suspension-ai-security";
import gilchristWinningMindset from "./gilchrist-winning-mindset";
import peopleAsMicroservices from "./people-as-microservices";
import innovationCredibilitySponsorRoi from "./innovation-credibility-sponsor-roi";
import managingAmbiguitySelfCompassion from "./managing-ambiguity-self-compassion";
import aiAllianceRoadmap from "./ai-alliance-roadmap";
import threeQuestionsBeforeDeploy from "./three-questions-before-you-deploy-ai";
import complianceAsDiscipline from "./compliance-as-discipline";
import nistTevvGap from "./nist-tevv-gap";
import aiThatDoesTheWork from "./ai-that-does-the-work";
import halfLifeOfInnovation from "./half-life-of-innovation-hoa-chatbot";
import leadershipMagnifiedMentorship from "./leadership-magnified-mentorship";
import bijliAaneWaliHai from "./bijli-aane-wali-hai-ai-in-india";
import parisTransitInclusiveDesign from "./paris-transit-inclusive-design";
import mentorshipMomentum from "./mentorship-momentum";
import closingTheGaapNotebooklm from "./closing-the-gaap-notebooklm";
import githubPagesHosting from "./github-pages-hosting";
import imposterSyndromeIdentityGap from "./imposter-syndrome-identity-gap";
import yourTechExecIsAUserToo from "./your-tech-exec-is-a-user-too";
import vibeCodingAdhdDashboard from "./vibe-coding-adhd-dashboard";
import unpluggedClarityHalfMarathon from "./unplugged-clarity-half-marathon";
import wiserBiteSameFoodDifferentWisdom from "./wiser-bite-same-food-different-wisdom";
import parentingOsPrivateByDesign from "./parenting-os-private-by-design";
import aiIsNotMagic from "./ai-is-not-magic";
import askTheConstitution from "./ask-the-constitution-browser-native";
import understandLiechtenstein from "./understand-liechtenstein";
import everyoneInTheRoomGetsToSing from "./everyone-in-the-room-gets-to-sing";
import pathToProduction from "./path-to-production";
import meatProxySelfCheck from "./meat-proxy-self-check";
import ascendNjNetworkingBingo from "./ascend-nj-networking-bingo";
import weighingTheMoon from "./weighing-the-moon";
import policyToControlMeasuringAi from "./policy-to-control-measuring-ai";
import theStupidTax from "./the-stupid-tax";
import wiringTheMoon from "./wiring-the-moon";
import brokenBottleRemote3dPrint from "./broken-bottle-remote-3d-print";
import jevlisKaBuildingWithJev from "./jevlis-ka-building-with-jev";
import theSkillChangesNotTheModel from "./the-skill-changes-not-the-model";

const modules = [
	orangeRibbonHunt,
	momentumOneSmallThing,
	guardrailReviewBathroomLockout,
	nobelExplainerAgentPipeline,
	theSkillChangesNotTheModel,
	jevlisKaBuildingWithJev,
	brokenBottleRemote3dPrint,
	wiringTheMoon,
	theStupidTax,
	everyoneInTheRoomGetsToSing,
	policyToControlMeasuringAi,
	weighingTheMoon,
	ascendNjNetworkingBingo,
	meatProxySelfCheck,
	pathToProduction,
	understandLiechtenstein,
	askTheConstitution,
	aiIsNotMagic,
	parentingOsPrivateByDesign,
	wiserBiteSameFoodDifferentWisdom,
	measuringAiPrinciples,
	theAccountabilitySplit,
	raciDaciDelay,
	softwareSupplyChainRisk,
	humaneInTheLoop,
	oneMorePrompt,
	proofOfWorkVsImposter,
	singaporeFrontRowSeat,
	tokenOptimizingClaudeCode,
	owaspAgentSecurityGap,
	gcpSuspensionAiSecurity,
	gilchristWinningMindset,
	peopleAsMicroservices,
	innovationCredibilitySponsorRoi,
	managingAmbiguitySelfCompassion,
	aiAllianceRoadmap,
	threeQuestionsBeforeDeploy,
	complianceAsDiscipline,
	nistTevvGap,
	aiThatDoesTheWork,
	halfLifeOfInnovation,
	leadershipMagnifiedMentorship,
	bijliAaneWaliHai,
	parisTransitInclusiveDesign,
	mentorshipMomentum,
	closingTheGaapNotebooklm,
	githubPagesHosting,
	imposterSyndromeIdentityGap,
	yourTechExecIsAUserToo,
	vibeCodingAdhdDashboard,
	unpluggedClarityHalfMarathon,
];

const WORDS_PER_MINUTE = 200;

function computeReadingTime(markdown) {
	const words = (markdown || "").trim().split(/\s+/).filter(Boolean).length;
	return Math.max(1, Math.round(words / WORDS_PER_MINUTE));
}

// Curated topic per article, powering the filter chips on /articles. One
// bucket each; adjust a slug here to move an article between chips.
export const TOPICS = [
	"AI Governance",
	"Leadership",
	"Building",
	"Reflections",
];

const TOPIC_BY_SLUG = {
	"momentum-one-small-thing": "Building",
	"broken-bottle-remote-3d-print": "Building",
	"wiring-the-moon": "Building",
	"the-stupid-tax": "Leadership",
	"everyone-in-the-room-gets-to-sing": "Building",
	"measuring-ai-principles": "AI Governance",
	"the-accountability-split": "Leadership",
	"raci-daci-delay": "Leadership",
	"software-supply-chain-risk": "AI Security",
	"humane-in-the-loop": "AI Governance",
	"one-more-prompt": "Reflections",
	"proof-of-work-vs-imposter-syndrome": "Reflections",
	"singapore-front-row-seat": "Reflections",
	"token-optimizing-claude-code": "Building",
	"owasp-agent-security-gap": "AI Security",
	"gcp-suspension-ai-security": "AI Security",
	"gilchrist-winning-mindset": "Reflections",
	"people-as-microservices": "Leadership",
	"innovation-credibility-sponsor-roi": "Leadership",
	"managing-ambiguity-self-compassion": "Reflections",
	"ai-alliance-roadmap": "AI Governance",
	"three-questions-before-you-deploy-ai": "AI Governance",
	"compliance-as-discipline": "AI Governance",
	"nist-tevv-gap": "AI Governance",
	"ai-that-does-the-work": "Building",
	"half-life-of-innovation-hoa-chatbot": "Building",
	"leadership-magnified-mentorship": "Leadership",
	"bijli-aane-wali-hai-ai-in-india": "Reflections",
	"paris-transit-inclusive-design": "Building",
	"mentorship-momentum": "Leadership",
	"closing-the-gaap-notebooklm": "Building",
	"github-pages-hosting": "Building",
	"imposter-syndrome-identity-gap": "Reflections",
	"your-tech-exec-is-a-user-too": "Leadership",
	"vibe-coding-adhd-dashboard": "Building",
	"unplugged-clarity-half-marathon": "Reflections",
	"wiser-bite-same-food-different-wisdom": "Building",
	"parenting-os-private-by-design": "Building",
	"ai-is-not-magic": "Building",
	"ask-the-constitution-browser-native": "Building",
	"understand-liechtenstein": "Building",
	"path-to-production": "AI Governance",
	"meat-proxy-self-check": "Building",
	"ascend-nj-networking-bingo": "Building",
	"weighing-the-moon": "Building",
	"policy-to-control-measuring-ai": "AI Governance",
};

const myArticles = modules.map((article) => ({
	...article,
	readingTime: article.readingTime || computeReadingTime(article.markdown),
	topic: TOPIC_BY_SLUG[article.slug] || "More",
}));

export function getArticleBySlug(slug) {
	return myArticles.find((article) => article.slug === slug) || null;
}

export function getArticlesInSeries(series) {
	if (!series) return [];
	return myArticles.filter((article) => article.series === series);
}

// Related articles: same series scores highest, then shared keywords, then
// recency fills any remaining slots so every article shows a full set.
export function getRelatedArticles(slug, limit = 3) {
	const current = getArticleBySlug(slug);
	if (!current) return [];

	const currentKeywords = new Set(
		(current.keywords || []).map((k) => k.toLowerCase())
	);

	const scored = myArticles
		.filter((a) => a.slug !== slug)
		.map((a) => {
			let score = 0;
			if (current.series && a.series === current.series) score += 5;
			(a.keywords || []).forEach((k) => {
				if (currentKeywords.has(k.toLowerCase())) score += 1;
			});
			return { article: a, score };
		})
		.sort((x, y) => y.score - x.score);

	const related = scored
		.filter((s) => s.score > 0)
		.map((s) => s.article);

	if (related.length < limit) {
		for (const a of myArticles) {
			if (a.slug !== slug && !related.includes(a)) related.push(a);
			if (related.length >= limit) break;
		}
	}

	return related.slice(0, limit);
}

export default myArticles;
